import { useCallback, useEffect, useRef, useState } from 'react';
import { apiGet } from '../client';
import { useSessionUser } from '../../store/authStore';
import { useMasterPasswordStore } from '@/store/masterPasswordStore';
import type { User } from '@/@types/auth';

type CurrentUserResponse = {
  user: User;
};

export function useCurrentUser() {
  const setUser = useSessionUser((s) => s.setUser);
  const setSessionSignedIn = useSessionUser((s) => s.setSessionSignedIn);
  const setConfigured = useMasterPasswordStore((s) => s.setConfigured);
  const setHasTtl = useMasterPasswordStore((s) => s.setHasTtl);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ctrlRef = useRef<AbortController | null>(null);

  const refetch = useCallback(async () => {
    // cancel previous in-flight request
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;

    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<CurrentUserResponse>(
        '/app/v1/user/me'
      );
      setUser(data.user);
      setConfigured(data.user.hasMasterPassword ?? false);
      setHasTtl(data.user.hasTtl ?? false);
      setSessionSignedIn(true);
    } catch (e: any) {
      if (e?.name === 'AbortError') return;
      setError(e?.message || 'Failed to load user');
      setSessionSignedIn(false);
    } finally {
      if (ctrlRef.current === ctrl) ctrlRef.current = null;
      setLoading(false);
    }
  }, [setUser, setSessionSignedIn, setConfigured, setHasTtl]);

  useEffect(() => {
    void refetch();
    return () => ctrlRef.current?.abort();
  }, [refetch]);

  return { loading, error, refetch };
}
